import React, { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, CircularProgress,
} from '@mui/material';
import config from '../../config';
import { toast } from 'react-toastify';

// Booking Ledger / Audit page (#4) - search a Booking ID and see the complete
// booking picture: details, customer, package, discount, the full CR/DR ledger
// and the final paid/pending summary. Read-only; reuses existing endpoints
// (Getbookingbyid + GetBookingDetails) so it adds no new backend surface.

const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return isNaN(dt) ? '-' : dt.toLocaleString('en-GB');
};
const fmtDay = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return isNaN(dt) ? '-' : dt.toLocaleDateString('en-GB');
};
const money = (n) => Number(n || 0).toFixed(2);

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return undefined;
};

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4 }}>
    <Typography variant='body2' color='text.secondary'>{label}</Typography>
    <Typography variant='body2' fontWeight={600} textAlign='right'>
      {value == null || value === '' ? '-' : value}
    </Typography>
  </Box>
);

const SectionCard = ({ title, children }) => (
  <Paper variant='outlined' sx={{ p: 2, borderRadius: 2, height: '100%' }}>
    <Typography variant='subtitle2' color='primary' fontWeight={700} mb={1}>{title}</Typography>
    {children}
  </Paper>
);

export default function BookingLedger() {
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    const id = (bookingId || '').toString().trim();
    if (!id) { toast.warn('Please enter a Booking ID'); return; }
    setLoading(true);
    setSearched(true);
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [bRes, lRes] = await Promise.all([
        fetch(`${config.API_BASE_URL}/Home/Getbookingbyid?Bookingid=${id}`, { headers }),
        fetch(`${config.API_BASE_URL}/Home/GetBookingDetails/${id}`, { headers }),
      ]);
      const bj = bRes.ok ? await bRes.json() : null;
      const lj = lRes.ok ? await lRes.json() : [];
      const bookingObj = Array.isArray(bj) ? bj[0] : bj;
      if (!bookingObj || !pick(bookingObj, 'id', 'Id', 'bookingId', 'BookingId')) {
        toast.error('Booking not found');
        setBooking(null); setLedger([]); return;
      }
      setBooking(bookingObj);
      setLedger(Array.isArray(lj) ? lj : []);
    } catch (e) {
      toast.error('Failed to load booking ledger');
      setBooking(null); setLedger([]);
    } finally {
      setLoading(false);
    }
  };

  const b = booking || {};
  const totalCR = ledger
    .filter((e) => (pick(e, 'type', 'Type') || '').toUpperCase() === 'CR')
    .reduce((s, e) => s + Number(pick(e, 'amount', 'Amount') || 0), 0);
  const totalDR = ledger
    .filter((e) => (pick(e, 'type', 'Type') || '').toUpperCase() === 'DR')
    .reduce((s, e) => s + Number(pick(e, 'amount', 'Amount') || 0), 0);
  const finalAmount = Number(pick(b, 'finalpriceWithGST', 'FinalpriceWithGST', 'dicountedprice', 'Dicountedprice') || 0);
  const paid = totalCR;
  const pending = totalDR - totalCR;

  const guestName = [pick(b, 'title', 'Title'), pick(b, 'firstName', 'FirstName'), pick(b, 'lastName', 'LastName')]
    .filter(Boolean).join(' ');

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant='h5' fontWeight={700} mb={0.5}>Booking Ledger</Typography>
      <Typography variant='body2' color='text.secondary' mb={2}>
        Complete booking + payment audit. Search a Booking ID.
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField size='small' label='Booking ID' value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()} sx={{ minWidth: 220 }} />
        <Button variant='contained' onClick={search} disabled={loading}>
          {loading ? <CircularProgress size={22} color='inherit' /> : 'Submit'}
        </Button>
      </Paper>

      {searched && !loading && !booking && (
        <Typography color='text.secondary'>No booking found for this ID.</Typography>
      )}

      {booking && (
        <>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <SectionCard title='Booking Details'>
                <Row label='Booking ID' value={pick(b, 'id', 'Id', 'bookingId', 'BookingId')} />
                <Row label='Booking Date' value={fmtDay(pick(b, 'createdAt', 'CreatedAt'))} />
                <Row label='Check-In' value={fmtDate(pick(b, 'checkIn', 'CheckIn'))} />
                <Row label='Check-Out' value={fmtDate(pick(b, 'checkOut', 'CheckOut'))} />
                <Row label='Hotel' value={pick(b, 'hotelName', 'HotelName')} />
                <Row label='Rooms' value={pick(b, 'roomNumbers', 'RoomNumbers', 'roomNumber')} />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <SectionCard title='Customer Details'>
                <Row label='Name' value={guestName} />
                <Row label='Mobile' value={pick(b, 'mobileNumber', 'MobileNumber')} />
                <Row label='Address' value={pick(b, 'address', 'Address')} />
                <Row label='Company' value={pick(b, 'companyname', 'Companyname')} />
                <Row label='GST No' value={pick(b, 'gstno', 'Gstno')} />
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <SectionCard title='Package / Charges'>
                <Row label='Package' value={pick(b, 'packageName', 'PackageName', 'packageId', 'PackageId')} />
                <Row label='Base Price' value={money(pick(b, 'baseprice', 'Baseprice'))} />
                <Row label='Discount Type' value={pick(b, 'discountType', 'DiscountType', 'extraDiscountType')} />
                <Row label='Discount Value' value={money(pick(b, 'discountValue', 'DiscountValue', 'extraDiscountValue'))} />
                <Row label='Coupon' value={pick(b, 'coupen', 'Coupen')} />
                <Row label='Coupon Amount' value={money(pick(b, 'couponAmount', 'CouponAmount'))} />
              </SectionCard>
            </Grid>
          </Grid>

          <Paper variant='outlined' sx={{ borderRadius: 2, mb: 2, overflow: 'auto' }}>
            <Typography variant='subtitle2' color='primary' fontWeight={700} sx={{ p: 2, pb: 1 }}>
              Payment Ledger (CR / DR)
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align='right'>Amount</TableCell>
                  <TableCell>Narration</TableCell>
                  <TableCell>Created By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align='center' sx={{ py: 2 }}>No ledger entries</TableCell>
                  </TableRow>
                ) : (
                  ledger.map((e, i) => {
                    const type = (pick(e, 'type', 'Type') || '').toUpperCase();
                    return (
                      <TableRow key={i}>
                        <TableCell>{fmtDate(pick(e, 'tranjectiondate', 'Tranjectiondate', 'created', 'Created'))}</TableCell>
                        <TableCell>{pick(e, 'mode', 'Mode') || '-'}</TableCell>
                        <TableCell>
                          <Chip label={type || '-'} size='small'
                            color={type === 'CR' ? 'success' : 'error'} variant='outlined' />
                        </TableCell>
                        <TableCell align='right'>{money(pick(e, 'amount', 'Amount'))}</TableCell>
                        <TableCell>{pick(e, 'remark', 'Remark') || '-'}</TableCell>
                        <TableCell>{pick(e, 'username', 'Username') || '-'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Paper>

          <Grid container justifyContent='flex-end'>
            <Grid item xs={12} md={5}>
              <SectionCard title='Final Summary'>
                <Row label='Final Amount' value={money(finalAmount)} />
                <Row label='Total Debit (DR)' value={money(totalDR)} />
                <Row label='Total Paid (CR)' value={money(paid)} />
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography fontWeight={700}>Pending</Typography>
                  <Typography fontWeight={700} color={pending > 0.5 ? 'error.main' : 'success.main'}>
                    {money(pending)}
                  </Typography>
                </Box>
              </SectionCard>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}